/**
 * Board E · „Lista z odhaczeniem" (canon `docs/design/ai-buddy-2026-10-09-proposed-exercise/`,
 * ai-plan-builder R8): the draft's exercises outside the catalogue as one grouped card under its
 * program card. The header counts what is resolved („Rozwiązane n z N", rolling digits and one
 * segment per name), one row is open at a time, and the foot offers „Dodaj wszystkie szybko" (asked
 * first, every name with its proposal) or says the draft is about to open.
 *
 * `PickChangeCard` is the same row alone in a card: one of Buddy's own picks opened to change under
 * „Założyłem".
 */

import clsx from 'clsx';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_EXERCISE_NAME_LENGTH } from '~/entities/exercise-library';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { proposalLine } from '../lib/outsideCatalogueWording';
import type { DraftDecisions } from '../lib/useExerciseDecisions';

import { ExerciseDecisionRow } from './ExerciseDecisionRow';
import { RollingNumber } from './RollingNumber';
import surfaces from './surfaces.module.css';
import styles from './OutsideCatalogue.module.css';

/** The rows resolved before the list was drawn stand still, until the trainer reopens one. */
function useQuietRows(decisions: DraftDecisions) {
  const [quiet, setQuiet] = useState<ReadonlySet<string>>(
    () => new Set(decisions.view.rows.filter((row) => row.resolved).map((row) => row.exercise.name)),
  );
  return {
    quiet,
    wake: (name: string) => {
      setQuiet((current) => {
        const next = new Set(current);
        next.delete(name);
        return next;
      });
    },
  };
}

export function OutsideCatalogue({ decisions }: { readonly decisions: DraftDecisions }) {
  const { t } = useTranslation();
  const { view } = decisions;
  const { quiet, wake } = useQuietRows(decisions);
  const title = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const batchRefusalId = useId();
  const [asking, setAsking] = useState(false);
  // The names „Dodaj wszystkie szybko" resolved, in the order their checks follow one another.
  const [batch, setBatch] = useState<ReadonlyMap<string, number>>(new Map());
  const names = (list: readonly { readonly name: string }[]) => list.map((each) => t('web:aiPlan.unresolved.name', { name: each.name })).join(', ');
  const { quickAdd } = decisions;
  // A failed add on a folded row has no sentence of its own on screen: the status names it. The
  // open row says its own failure in its panel.
  const failedFolded = view.rows.filter((row) => row.failure !== null && !row.expanded).map((row) => row.exercise);
  // The one status region: the count, what was not added, and the draft about to open, said before
  // the page changes under the trainer (WCAG 3.2.2).
  const status = [
    t('web:aiPlan.unresolved.counter', { resolved: view.resolvedCount, total: view.total }),
    ...(failedFolded.length > 0 ? [t('web:aiPlan.unresolved.failedNames', { names: names(failedFolded) })] : []),
    ...(decisions.opening ? [t('web:aiPlan.unresolved.openingStatus')] : []),
  ].join(' ');

  return (
    <section className={clsx(surfaces.card, surfaces.riseCard, styles.group)} aria-labelledby={titleId}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <h4 ref={title} id={titleId} className={styles.title} tabIndex={-1}>
            {view.allResolved ? t('web:aiPlan.unresolved.titleDone') : t('web:aiPlan.unresolved.title')}
          </h4>
          <p className={styles.small}>{t('web:aiPlan.unresolved.lede')}</p>
        </div>
        <div className={styles.counter} role="status">
          <span className={styles.segments} aria-hidden="true">
            {view.rows.map((row, index) => (
              <i key={row.exercise.name} className={clsx(styles.segment, index < view.resolvedCount && styles.segmentOn)} />
            ))}
          </span>
          <span className={styles.count} aria-hidden="true">
            <RollingNumber value={view.resolvedCount} />
            <span>{t('web:aiPlan.unresolved.counterOf', { total: view.total })}</span>
          </span>
          <span className={hidden.srOnly}>{status}</span>
        </div>
      </div>
      <ul className={styles.rows}>
        {view.rows.map((row) => (
          <ExerciseDecisionRow
            key={row.exercise.name}
            row={row}
            decisions={decisions}
            quiet={quiet.has(row.exercise.name)}
            batchStep={batch.get(row.exercise.name) ?? null}
            onReopen={() => {
              wake(row.exercise.name);
              setBatch((current) => {
                const next = new Map(current);
                next.delete(row.exercise.name);
                return next;
              });
            }}
          />
        ))}
      </ul>
      <div className={styles.groupFoot}>
        {decisions.opening ? (
          <span className={styles.opening}>
            <span className={surfaces.spinner} aria-hidden="true" />
            {t('web:aiPlan.unresolved.opening')}
          </span>
        ) : view.allResolved ? (
          <span />
        ) : (
          <p className={styles.small}>{t('web:aiPlan.unresolved.hint')}</p>
        )}
        {/* The builder's panel adds nothing to the catalogue: each row hands its name to the Buddy page. */}
        {view.canBatch && quickAdd !== null && (
          <button
            type="button"
            className={clsx(surfaces.button, surfaces.small)}
            aria-disabled={view.batchRefused ? 'true' : undefined}
            aria-describedby={view.batchRefused ? batchRefusalId : undefined}
            onClick={() => {
              if (!view.batchRefused) setAsking(true);
            }}
          >
            {t('web:aiPlan.unresolved.addAll')}
          </button>
        )}
        <span id={batchRefusalId} className={hidden.srOnly}>
          {view.batchRefused ? t('web:aiPlan.unresolved.saving') : ''}
        </span>
      </div>
      <ConfirmDialog
        prompt={
          asking
            ? {
                title: t('web:aiPlan.unresolved.batch.title', { count: view.batchable.length }),
                description: t('web:aiPlan.unresolved.batch.lede'),
                details: (
                  <>
                    <ul className={styles.batchList}>
                      {view.batchable.map((exercise) => (
                        <li key={exercise.name} className={styles.batchItem}>
                          <span className={styles.batchName}>{t('web:aiPlan.unresolved.name', { name: exercise.name })}</span>
                          {exercise.proposedExercise !== null && (
                            <span className={styles.batchLine}>{proposalLine(t, exercise.proposedExercise)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                    {view.withoutProposal.length > 0 && (
                      <p className={styles.noProposal}>
                        {t('web:aiPlan.unresolved.batch.missing', {
                          count: view.withoutProposal.length,
                          names: names(view.withoutProposal),
                        })}
                      </p>
                    )}
                    {view.nameTooLong.length > 0 && (
                      <p className={styles.noProposal}>
                        {t('web:aiPlan.unresolved.batch.tooLong', {
                          count: view.nameTooLong.length,
                          names: names(view.nameTooLong),
                          max: MAX_EXERCISE_NAME_LENGTH,
                        })}
                      </p>
                    )}
                  </>
                ),
                confirmLabel: t('web:aiPlan.unresolved.batch.confirm', { count: view.batchable.length }),
                cancelLabel: t('web:aiPlan.unresolved.batch.cancel'),
              }
            : null
        }
        onConfirm={() => {
          setAsking(false);
          if (view.batchRefused || quickAdd === null || view.batchable.length === 0) return;
          setBatch(new Map(view.batchable.map((exercise, index) => [exercise.name, index])));
          void quickAdd(view.batchable);
        }}
        onCancel={() => {
          setAsking(false);
        }}
        // The button that asked goes once the names are added: the group's title takes focus.
        finalFocus={title}
      />
    </section>
  );
}

/**
 * One of Buddy's own picks opened to change (R8), under „Założyłem": the same row, always open while
 * it waits; its header, „Zostaw wybór Buddy’ego" and Escape close it with nothing sent.
 */
export function PickChangeCard({
  decisions,
  id,
  onLeave,
}: {
  readonly decisions: DraftDecisions;
  /** The card's id, for the „Zmień" that opened it (`aria-controls`). */
  readonly id: string;
  /** While it is still Buddy's pick: closing the card leaves it so. A decided card has none. */
  readonly onLeave?: () => void;
}) {
  const { quiet, wake } = useQuietRows(decisions);
  const row = decisions.view.rows[0];
  if (row === undefined) return null;
  return (
    <div id={id} className={clsx(surfaces.card, surfaces.riseCard, styles.group, styles.single)}>
      <ul className={styles.rows}>
        <ExerciseDecisionRow
          row={row}
          decisions={decisions}
          quiet={quiet.has(row.exercise.name)}
          batchStep={null}
          onReopen={() => {
            wake(row.exercise.name);
          }}
          onLeave={onLeave}
        />
      </ul>
    </div>
  );
}
